/**
 * VALIDATION - Clean Architecture
 * 
 * Zod schemas para validación de datos
 * TODA data que entra/sale pasa por aquí
 */

import { z } from 'zod';
import { UserRole, PatientStatus, AssignmentFrequency, PracticeVisibility, PracticeDifficulty, SessionType } from '../types/enums';

// ============================================
// USER SCHEMAS
// ============================================

export const CreateUserSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(6, 'Contraseña debe tener mínimo 6 caracteres'),
  role: z.nativeEnum(UserRole)
});

export const UpdateUserProfileSchema = z.object({
  name: z.string().min(2).max(100).optional(),
  bio: z.string().max(500).optional(),
  mainGoal: z.string().max(200).optional(),
  interests: z.array(z.string()).optional(),
  preferences: z.object({
    language: z.enum(['es', 'en']).optional(),
    notifications: z.boolean().optional(),
    theme: z.enum(['light', 'dark']).optional()
  }).optional()
});

// ============================================
// THERAPIST SCHEMAS
// ============================================

export const CreateTherapistProfileSchema = z.object({
  name: z.string().min(3, 'Nombre muy corto').max(100),
  title: z.string().min(3, 'Título muy corto').max(100),
  license: z.string()
    .regex(/^[A-Z]{2,4}-\d{4,6}$/, 'Formato de licencia inválido (ej: PSI-12345)'),
  yearsExperience: z.number().min(0).max(50),
  specialties: z.array(z.string()).min(1, 'Selecciona al menos 1 especialidad'),
  approaches: z.array(z.string()).min(1, 'Selecciona al menos 1 enfoque'),
  sessionPrice: z.number().min(0).max(500),
  bio: z.string().min(50, 'Bio debe tener mínimo 50 caracteres').max(1000),
  firstSessionFree: z.boolean().optional().default(true),
  acceptsInsurance: z.boolean().optional().default(false),
  isPublic: z.boolean().optional().default(true),
  acceptingNewPatients: z.boolean().optional().default(true)
});

export const UpdateTherapistProfileSchema = CreateTherapistProfileSchema.partial();

// ============================================
// PATIENT SCHEMAS
// ============================================

export const CreatePatientSchema = z.object({
  name: z.string().min(2).max(100),
  email: z.string().email(),
  phone: z.string().optional(),
  diagnosis: z.string().max(500).optional(),
  goals: z.array(z.string()).min(1, 'Debe tener al menos 1 objetivo'),
  privateNotes: z.string().max(2000).optional()
});

export const UpdatePatientSchema = CreatePatientSchema.partial().extend({
  status: z.nativeEnum(PatientStatus).optional(),
  nextSessionDate: z.string().optional()
});

// ============================================
// PRACTICE SCHEMAS
// ============================================

export const CreatePracticeSchema = z.object({
  title: z.string().min(3).max(100),
  description: z.string().min(10).max(500),
  instructions: z.array(z.string()).min(1),
  duration: z.number().min(1).max(120), // Max 2 horas
  difficulty: z.nativeEnum(PracticeDifficulty),
  category: z.string(),
  tags: z.array(z.string()),
  amarId: z.string(),
  visibility: z.nativeEnum(PracticeVisibility)
});

export const UpdatePracticeSchema = CreatePracticeSchema.partial();

// ============================================
// ASSIGNMENT SCHEMAS
// ============================================

export const AssignPracticeSchema = z.object({
  patientId: z.string(),
  practiceId: z.string(),
  frequency: z.nativeEnum(AssignmentFrequency),
  duration: z.number().min(1).max(90).optional(),
  note: z.string().max(500).optional()
});

// ============================================
// SESSION SCHEMAS
// ============================================

export const CreateSessionSchema = z.object({
  patientId: z.string(),
  date: z.string(),
  duration: z.number().min(15).max(180), // 15min a 3h
  type: z.nativeEnum(SessionType),
  notes: z.string().max(5000),
  nextSteps: z.array(z.string()).optional()
});

// ============================================
// ROLE MANAGEMENT SCHEMAS (Admin)
// ============================================

export const ChangeUserRoleSchema = z.object({
  userId: z.string(),
  newRole: z.nativeEnum(UserRole),
  addRole: z.boolean().optional().default(false)
});

export const UpdatePermissionsSchema = z.object({
  userId: z.string(),
  permissions: z.array(z.string())
});

// ============================================
// KNOWLEDGE BASE SCHEMAS
// ============================================

export const CreateKnowledgeBaseSchema = z.object({
  type: z.enum(['note', 'document', 'image']),
  source: z.enum(['camera', 'upload', 'ocr', 'manual']),
  title: z.string().min(1),
  description: z.string().optional(),
  originalText: z.string().optional(),
  tags: z.array(z.string()).optional(),
  amarId: z.string().optional(),
  imageUrls: z.array(z.string()).optional(),
  documentUrls: z.array(z.string()).optional()
});

export const SearchKnowledgeBaseSchema = z.object({
  query: z.string().min(1),
  type: z.enum(['note', 'document', 'image']).optional(),
  amarId: z.string().optional(),
  amarCategory: z.string().optional(),
  tags: z.array(z.string()).optional(),
  limit: z.number().positive().optional()
});

// ============================================
// APPOINTMENT SCHEMAS
// ============================================

export const CreateAppointmentSchema = z.object({
  patientId: z.string(),
  therapistId: z.string(),
  dateTime: z.string(),
  duration: z.number().min(15).max(180),
  type: z.nativeEnum(SessionType),
  notes: z.string().max(500).optional()
});

// ============================================
// HELPER: Validate function
// ============================================

// Validate helper extracted to @boosting/core but re-exported from index.
// To fix imports that point specifically here:
export { validate } from '@boosting/core';
